/**
 * Allow the user to enable and disable two-factor authentication as a setting.
 *
 * @module Two-Factor Authentication
 * @format
 * @see [2FA - Wikipedia](https://www.wikipedia.org/wiki/2FA)
 */

import {CONNECT, COMPILE, SCOPE} from '@acng/frontend-stargazer';
import {on, INPUT, KEYDOWN} from '@acng/frontend-bounty/dom/event.js';
import {guard, IS, REGEX, BOOLEAN, OBJECT, STRING} from '@acng/frontend-rubicon';
import {isDefined} from '@acng/frontend-bounty/std/value.js';
import {set} from '@acng/frontend-bounty/dom/attribute.js';
import {query, queryAll} from '@acng/frontend-bounty/dom/query.js';
import {BUTTON} from '@acng/frontend-bounty/dom/element.js';
import {iterate} from '@acng/frontend-bounty/object.js';
import {SubmitEventController} from 'nova';
import {ClassTokenController, RemoveController, ValueController} from 'nova';
/**
 * @import {Controller} from '@acng/frontend-stargazer'
 */
import {get, post, put} from '../core/service/backend.js';
import {AUTH_USER_DATA} from '../model';
import {getLocalOverlay} from '../core/service/overlay.js';
import {performLogin} from './login.js';

/**
 * @implements {Controller}
 */
class OneTimeCodeForm extends SubmitEventController {
  /**
   * @callback OneTimeCodeAction
   * @param {HTMLFormElement} form
   * @param {string} code
   * @returns {unknown}
   */

  /**
   * @param {OneTimeCodeAction} action
   */
  constructor(action) {
    super(async (form) => {
      const code = OneTimeCodeForm.#getInputCode(form);
      ASSERT: guard(code, REGEX(/^\d{6,6}$/), 'two factor authentication code');
      await action(form, code);
      form.reset();
    });
  }

  /**
   * Upgrade all `<input>`. Responsive `<button>`
   *
   * @param {Element} element
   * @override
   */
  [CONNECT](element) {
    super[CONNECT](element);

    const button = query(element, BUTTON);
    ASSERT: guard(button, IS(HTMLButtonElement));

    button.disabled = true;

    on(element, INPUT, () => {
      const value = OneTimeCodeForm.#getInputCode(element);
      button.disabled = value.length != 6;
    });

    OneTimeCodeForm.#upgradeAllInputElements(element);
  }

  /**
   * @param {Element} form
   * @returns {string} Six decimal digits
   */
  static #getInputCode(form) {
    let code = '';
    iterate(queryAll(form, INPUT), (input) => (code += input.value));

    return code;
  }

  /**
   * @param {ParentNode} parentNode
   * @returns {NodeListOf<HTMLInputElement>}
   */
  static #upgradeAllInputElements(parentNode) {
    const inputList = queryAll(parentNode, INPUT);
    inputList.forEach(OneTimeCodeForm.#upgradeInput);

    return inputList;
  }

  /**
   * @param {HTMLInputElement} input
   * @param {number} index
   * @param {NodeListOf<HTMLInputElement> | HTMLInputElement[]} list
   */
  static #upgradeInput(input, index, list) {
    /**
     * @type {HTMLInputElement | undefined}
     */
    const prevInput = list[index - 1];

    /**
     * @type {HTMLInputElement | undefined}
     */
    const nextInput = list[index + 1];

    set(input, 'type', 'number');
    set(input, 'autocomplete', index ? 'off' : 'one-time-code');
    set(input, 'name', `code`);
    set(input, 'required');
    set(input, 'min', '0');
    set(input, 'max', '9');

    on(input, KEYDOWN, ({code}) => {
      if (code == 'Backspace' && input.value == '') {
        prevInput?.focus();
      }
    });

    on(input, INPUT, () => {
      const [first, ...rest] = input.value;
      input.value = first ?? '';

      if (isDefined(first) && nextInput) {
        nextInput.focus();
        nextInput.value = rest.join('');
        nextInput.dispatchEvent(new InputEvent(INPUT));
      }
    });
  }
}

/**
 * Two Factor Auth Config Form
 *
 * @implements {Controller}
 */
class TwoFactorConfig extends OneTimeCodeForm {
  active = new RemoveController(false);
  inactive = new RemoveController(true);
  style = new ClassTokenController('two-factor-disabled', 'two-factor-enabled');

  /**
   * @type {ValueController<string | undefined>}
   */
  qrcode = new ValueController('');

  /**
   * @type {ValueController<string | undefined>}
   */
  key = new ValueController('');

  [SCOPE] = this;

  constructor() {
    super(async (form, code) => {
      const putActive = !this.#active;

      try {
        if (!this.#active) {
          await post('user/2fa-setup', {code}, form);
        } else {
          await put('user/2fa', {code, deactivate: true}, form);
        }

        this.#active = putActive;
      } catch (e) {
        await this.#fetchAll();

        return;
      }

      if (!putActive) {
        await this.#fetchSetupData();
      }
    });
  }

  get #active() {
    return this.active.condition;
  }

  set #active(value) {
    this.active.condition = value;
    this.inactive.condition = !value;
    this.style.condition = !value;
  }

  async [COMPILE]() {
    await this.#fetchAll();
  }

  async #fetchAll() {
    if (!(await this.#fetchActivationState())) {
      await this.#fetchSetupData();
    }
  }

  async #fetchSetupData() {
    const response = await get('user/2fa-setup');
    ASSERT: guard(response, OBJECT({svg: STRING, key: STRING}));

    this.qrcode.value = 'data:image/svg+xml;base64,' + response.svg;
    this.key.value = response.key;
  }

  /**
   * @returns {Promise<boolean>}
   */
  async #fetchActivationState() {
    const response = await get('user/2fa');
    ASSERT: guard(response, OBJECT({active: BOOLEAN}));

    const {active} = response;
    this.#active = active;

    return active;
  }
}

/**
 * @example
 *
 * ```html
 * <!--@ ${auth.login.twoFactor} -->``````
 *
 * @implements {Controller}
 *
 * @todo
 *
 *   - Dictionary(DOM): "action", "focus"
 *   - Dictionary(Nova): "getInputCode"
 *   - Remove overlay close hack`````````
 */
class TwoFactorLogin extends OneTimeCodeForm {
  constructor() {
    super(TwoFactorLogin.#submit);
  }

  /**
   * @param {Element} form
   */
  [CONNECT](form) {
    super[CONNECT](form);
    query(form, INPUT)?.focus();
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} code
   */
  static async #submit(form, code) {
    const url = 'user/confirm-device';
    const userData = await post(url, {code}, form);
    ASSERT: guard(userData, AUTH_USER_DATA, `decoded ${url} response body`);
    performLogin(userData);
    getLocalOverlay(form)?.close();
  }
}

export {
  //
  TwoFactorConfig,
  TwoFactorLogin,
};
